/**
 * Centralized secret-scanning JSON helper (technical/07 §Access And Redaction
 * Checks; tech-debt #1).
 *
 * The validator / ToolBroker / action-card / access-runtime paths all need the
 * SAME secret posture: a JSON-text field that must (a) be valid JSON and (b)
 * carry no secret-bearing keys (`api_key`, `authorization`, `secret`, …). This
 * was copy-pasted verbatim into `access-runtime.ts` and `action-cards.ts`; both
 * now import from here so one regex / one recursive scan governs the boundary.
 *
 * Lifted verbatim from those files so behavior is byte-identical — do not weaken
 * the pattern or the recursion without updating every caller's contract.
 */

import { z } from 'zod';

/**
 * Keys whose presence (at any depth) marks a JSON blob as secret-bearing. The
 * boundary delimiters (`^|_|-`) and (`_|-|$`) keep the match on whole tokens so
 * a benign field like `summary` does not trip on the `secret`-shaped substring
 * `summa…` — only `…_secret`, `secret_…`, `api-key`, `apiKey` (via underscore
 * normalization upstream), etc. match.
 */
export const SECRET_KEY_PATTERN =
  /(^|_|-)(authorization|api[_-]?key|access[_-]?token|refresh[_-]?token|bearer|oauth|password|secret|secret[_-]?ref|webhook[_-]?secret)(_|-|$)/i;

/** True when `value` is valid JSON text (used to gate the secret scan). */
export function isJsonText(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    if (error instanceof SyntaxError) return false;
    return false;
  }
}

/**
 * Recursively walk a parsed JSON value; return true if any object key matches
 * `SECRET_KEY_PATTERN`. Arrays are walked element-wise; primitives never match.
 */
export function containsSecretKey(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) return value.some((item) => containsSecretKey(item));
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY_PATTERN.test(key)) return true;
    if (containsSecretKey(child)) return true;
  }
  return false;
}

/**
 * A string field that must hold valid JSON text with NO secret-bearing keys.
 * Shared by the ToolBroker request/result, action-card command previews, and the
 * AgentOutput envelope's `tool_call_requests[].sanitized_payload` so a secret can
 * never enter a packet / prompt / row / trace through any of those paths.
 *
 * `superRefine` parses ONCE and short-circuits on invalid JSON, so a malformed
 * payload reports a clean validation issue instead of letting `JSON.parse` throw
 * out of the refinement on `safeParse` (the latent behavior in the per-file
 * copies this module centralizes). Valid-JSON behavior is identical: secret-bearing
 * keys at any depth fail.
 */
export const sanitizedJsonTextSchema = z
  .string()
  .min(1)
  .superRefine((value, ctx) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'must be valid JSON text' });
      return;
    }
    if (containsSecretKey(parsed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'sanitized JSON must not contain secret-bearing keys',
      });
    }
  });
